import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { communityService } from '../../services/communityService';
import CreditBalanceWidget from '../CreditBalanceWidget';
import axios from 'axios';
import { 
  Dumbbell,
  Home, 
  User, 
  Apple, 
  MessageCircle,
  ChevronDown,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  MapPin,
  Utensils,
  Target,
  Trophy,
  Activity,
  Sparkles,
  BookOpen,
  CreditCard,
  Camera,
  Bell,
  Droplets,
  Flame
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  active?: boolean;
}

function NavLink({ to, icon, children, active = false }: NavLinkProps) {
  const baseClasses = 'flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors';
  const activeClasses = active 
    ? 'text-blue-600 bg-blue-50' 
    : 'text-gray-600 hover:text-blue-600';
  
  return (
    <Link to={to} className={`${baseClasses} ${activeClasses}`}>
      {icon}
      <span>{children}</span>
    </Link>
  );
}

function DropdownMenu({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
      >
        {icon}
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-20" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function DropdownLink({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
    >
      {icon}
      <span className="ml-2">{children}</span>
    </Link>
  );
}

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dispatch custom event when mobile menu opens/closes so BottomNavigation can hide
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: mobileMenuOpen } }));
  }, [mobileMenuOpen]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check lifestyle mode for conditional rendering
  // FII FIT = aplicație de fitness pură. Modul CHRONIC (medical) este complet
  // dezactivat — nu mai există ramuri condiționale în meniu.

  // Load profile photo
  // IMPORTANT: folosim user.id ca dependency și ÎNTOTDEAUNA facem fetch de la
  // server. Cache-ul din localStorage e folosit doar ca fallback instant (ca
  // să nu clipească UI-ul), dar valoarea reală vine de la API și suprascrie
  // cache-ul. Altfel, după logout + login pe alt cont, apărea poza contului
  // anterior (cache-ul nu era invalidat per user).
  useEffect(() => {
    if (!user) {
      setProfilePhotoUrl(null);
      return;
    }

    // Fallback instant din cache (poate fi învechit dacă user-ul tocmai s-a schimbat)
    const storedPhoto = localStorage.getItem('profilePhotoUrl');
    if (storedPhoto) {
      setProfilePhotoUrl(storedPhoto);
    }

    // Validează mereu cu serverul — sursa de adevăr este API-ul
    const loadProfilePhoto = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await axios.get(`${API_URL}/api/profile/photo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const serverPhoto: string | null = res.data?.profilePhotoUrl ?? null;
        setProfilePhotoUrl(serverPhoto);
        if (serverPhoto) {
          localStorage.setItem('profilePhotoUrl', serverPhoto);
        } else {
          localStorage.removeItem('profilePhotoUrl');
        }
      } catch (err) {
        // În caz de eroare (ex: offline) păstrăm valoarea din cache
      }
    };
    loadProfilePhoto();
  }, [user?.id]);

  // Fix EXIF orientation: reads orientation tag from JPEG and redraws the image
  // correctly on a canvas, returning a corrected Blob.
  const fixImageOrientation = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      // Only JPEG files can have EXIF orientation issues
      if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (!arrayBuffer) { resolve(file); return; }

        // Read EXIF orientation from the raw bytes
        const getOrientation = (buffer: ArrayBuffer): number => {
          const view = new DataView(buffer);
          if (view.getUint16(0, false) !== 0xFFD8) return 1; // Not a JPEG
          const length = view.byteLength;
          let offset = 2;
          while (offset < length) {
            if (view.getUint16(offset + 2, false) <= 8) return 1;
            const marker = view.getUint16(offset, false);
            offset += 2;
            if (marker === 0xFFE1) {
              if (view.getUint32(offset += 2, false) !== 0x45786966) return 1;
              const little = view.getUint16(offset += 6, false) === 0x4949;
              offset += view.getUint32(offset + 4, little);
              const tags = view.getUint16(offset, little);
              offset += 2;
              for (let i = 0; i < tags; i++) {
                if (view.getUint16(offset + i * 12, little) === 0x0112) {
                  return view.getUint16(offset + i * 12 + 8, little);
                }
              }
            } else if ((marker & 0xFF00) !== 0xFF00) {
              break;
            } else {
              offset += view.getUint16(offset, false);
            }
          }
          return 1;
        };

        const orientation = getOrientation(arrayBuffer);

        // If orientation is 1 (normal) or unknown, no fix needed
        if (orientation === 1) { resolve(file); return; }

        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(file); return; }

          // Set canvas dimensions based on orientation
          if (orientation >= 5 && orientation <= 8) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }

          // Apply the correct transform
          switch (orientation) {
            case 2: ctx.transform(-1, 0, 0, 1, img.width, 0); break;
            case 3: ctx.transform(-1, 0, 0, -1, img.width, img.height); break;
            case 4: ctx.transform(1, 0, 0, -1, 0, img.height); break;
            case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
            case 6: ctx.transform(0, 1, -1, 0, img.height, 0); break;
            case 7: ctx.transform(0, -1, -1, 0, img.height, img.width); break;
            case 8: ctx.transform(0, -1, 1, 0, 0, img.width); break;
            default: break;
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (!blob) { resolve(file); return; }
            const correctedFile = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
            resolve(correctedFile);
          }, 'image/jpeg', 0.92);
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
        img.src = objectUrl;
      };
      reader.onerror = () => resolve(file);
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle profile photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setPhotoUploadError('Imaginea este prea mare. Maxim 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setPhotoUploadError(null), 4000);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setPhotoUploadError('Tip de fișier invalid. Acceptăm: JPG, PNG, WebP, GIF, HEIC');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setPhotoUploadError(null), 4000);
      return;
    }

    setUploadingPhoto(true);
    setPhotoUploadError(null);
    try {
      const token = localStorage.getItem('accessToken');

      // Fix EXIF orientation before uploading - wrapped in try-catch to prevent failures on iOS
      let fileToUpload = file;
      try {
        fileToUpload = await fixImageOrientation(file);
      } catch (orientErr) {
        console.warn('fixImageOrientation failed, using original file:', orientErr);
        fileToUpload = file;
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const res = await axios.post(`${API_URL}/api/profile/upload-photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.profilePhotoUrl) {
        setProfilePhotoUrl(res.data.profilePhotoUrl);
        localStorage.setItem('profilePhotoUrl', res.data.profilePhotoUrl);
      }
    } catch (err: any) {
      console.error('Error uploading photo:', err);
      const msg = err.response?.data?.error || `Eroare: ${err.message || 'Încearcă din nou.'}`;
      setPhotoUploadError(msg);
      setTimeout(() => setPhotoUploadError(null), 5000);
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Get full photo URL
  const getFullPhotoUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${API_URL}${url}`;
  };

  // Load unread messages count - poll every 10 seconds for near real-time
  useEffect(() => {
    if (user) {
      const loadUnreadCount = async () => {
        try {
          const conversations = await communityService.getConversations();
          const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
          setUnreadMessages(totalUnread);
        } catch (err) {
          // Silently fail
        }
      };
      
      loadUnreadCount();
      // Refresh every 10 seconds for near real-time notifications
      const interval = setInterval(loadUnreadCount, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Listen for instant unread count updates (dispatched when messages are sent/received)
  useEffect(() => {
    const handleUnreadUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ unreadCount: number }>;
      if (typeof customEvent.detail?.unreadCount === 'number') {
        setUnreadMessages(customEvent.detail.unreadCount);
      }
    };
    const handleRefreshUnread = () => {
      communityService.getConversations().then(conversations => {
        const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
        setUnreadMessages(totalUnread);
      }).catch(() => {});
    };
    window.addEventListener('unread-messages-update', handleUnreadUpdate);
    window.addEventListener('refresh-unread-messages', handleRefreshUnread);
    return () => {
      window.removeEventListener('unread-messages-update', handleUnreadUpdate);
      window.removeEventListener('refresh-unread-messages', handleRefreshUnread);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isSpecialist = user?.userType === 'SPECIALIST';

  return (
    <nav
      className={`${isSpecialist ? 'bg-teal-700' : 'bg-white'} shadow-sm border-b ${isSpecialist ? 'border-teal-800' : 'border-gray-200'} sticky z-30`}
      style={{ top: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo & Desktop Nav */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Link to={isSpecialist ? '/specialist/dashboard' : '/dashboard'} className="flex items-center space-x-1.5 sm:space-x-2">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 ${isSpecialist ? 'bg-white/20' : 'bg-blue-600'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Dumbbell className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className={`text-lg sm:text-xl font-bold ${isSpecialist ? 'text-white' : 'text-gray-900'}`}>FII FIT</span>
              {isSpecialist && (
                <span className="hidden sm:inline text-xs font-semibold text-teal-200 bg-white/10 px-2 py-0.5 rounded-full ml-1">Business</span>
              )}
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 ml-6">
              {isSpecialist ? (
                // Specialist Navigation — white links on teal background
                <>
                  {[
                    { to: '/specialist/dashboard', icon: Home, label: 'Dashboard' },
                    { to: '/specialist/profile/edit', icon: User, label: 'Profilul Meu' },
                    { to: '/specialists', icon: MapPin, label: 'Hartă Specialiști' },
                    { to: '/specialist/settings', icon: Settings, label: 'Setări' },
                  ].map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </Link>
                  ))}
                </>
              ) : (
                // Patient Navigation - Optimizat cu grupare
                <>
                  <NavLink to="/dashboard" icon={<Home className="w-4 h-4" />} active>
                    Dashboard
                  </NavLink>
                  <NavLink to="/profile" icon={<User className="w-4 h-4" />}>
                    Profil
                  </NavLink>
                  
                  <DropdownMenu label="Nutriție" icon={<Apple className="w-4 h-4" />}>
                    <DropdownLink to="/nutrition" icon={<Apple className="w-4 h-4" />}>
                      Plan Nutriție
                    </DropdownLink>
                    <DropdownLink to="/food-tracking" icon={<Utensils className="w-4 h-4" />}>
                      Mese & Calorii cu AI
                    </DropdownLink>
                    <DropdownLink to="/food-community" icon={<Flame className="w-4 h-4" />}>
                      Retete Comunitate
                    </DropdownLink>
                  </DropdownMenu>
                  
                  <DropdownMenu label="Antrenament" icon={<Dumbbell className="w-4 h-4" />}>
                    <DropdownLink to="/exercises" icon={<Dumbbell className="w-4 h-4" />}>
                      Exerciții Video
                    </DropdownLink>
                    <DropdownLink to="/goals" icon={<Target className="w-4 h-4" />}>
                      Obiective Fitness
                    </DropdownLink>
                    <DropdownLink to="/challenges" icon={<Trophy className="w-4 h-4" />}>
                      Provocări
                    </DropdownLink>
                  </DropdownMenu>

                  <DropdownMenu label="Tracking" icon={<Activity className="w-4 h-4" />}>
                    <DropdownLink to="/water-intake" icon={<Droplets className="w-4 h-4" />}>
                      Aport Apă
                    </DropdownLink>
                    <DropdownLink to="/insights" icon={<Sparkles className="w-4 h-4" />}>
                      AI Insights
                    </DropdownLink>
                    <DropdownLink to="/progress-photos" icon={<Camera className="w-4 h-4" />}>
                      Fotografii Progres
                    </DropdownLink>
                    <DropdownLink to="/journal" icon={<BookOpen className="w-4 h-4" />}>
                      Jurnal (Tranzit & Notițe)
                    </DropdownLink>
                  </DropdownMenu>

                  
                  <DropdownMenu label="Resurse" icon={<MessageCircle className="w-4 h-4" />}>
                    <DropdownLink to="/chat" icon={<MessageCircle className="w-4 h-4" />}>
                      Nutriționist AI
                    </DropdownLink>
                    <DropdownLink to="/specialists" icon={<MapPin className="w-4 h-4" />}>
                      Hartă Specialiști
                    </DropdownLink>
                    <DropdownLink to="/education" icon={<BookOpen className="w-4 h-4" />}>
                      Educație Fitness
                    </DropdownLink>
                  </DropdownMenu>


                </>
              )}
            </div>
          </div>
          
          {/* Right Side - Profile Photo + Bell */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Credit Balance Widget - Only for patients (desktop + mobile) */}
            {user?.userType !== 'SPECIALIST' && (
              <CreditBalanceWidget />
            )}

            {/* Photo upload error toast */}
            {photoUploadError && (
              <div className="fixed top-16 right-4 z-50 bg-red-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg max-w-xs">
                {photoUploadError}
              </div>
            )}

            {/* Profile Photo with Camera Icon */}
            <div className="relative">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="relative group"
                title="Schimbă poza de profil"
              >
                {getFullPhotoUrl(profilePhotoUrl) ? (
                  <img
                    src={getFullPhotoUrl(profilePhotoUrl)!}
                    alt="Profil"
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-gray-200 group-hover:border-blue-400 transition-colors"
                  />
                ) : (
                  <img
                    src={`https://ui-avatars.com/api/?name=${user?.firstName || 'User'}&background=3B82F6&color=fff&size=88`}
                    alt="Avatar"
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border-2 border-gray-200 group-hover:border-blue-400 transition-colors"
                  />
                )}
                {/* Camera badge */}
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                  <Camera className="w-2.5 h-2.5 text-white" />
                </div>
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </div>

            {/* Notification Bell */}
            <button
              className={`relative p-2 rounded-lg transition-colors ${isSpecialist ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-purple-600 hover:bg-gray-100'}`}
              onClick={() => navigate('/notifications')}
              title="Notificari & Remindere"
            >
              <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
              {unreadMessages > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </button>
            
            {/* User Menu (desktop dropdown) */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 rounded-lg transition-colors ${isSpecialist ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <span className={`text-sm font-medium ${isSpecialist ? 'text-white' : 'text-gray-700'}`}>
                  {user?.firstName || user?.email?.split('@')[0]}
                </span>
                <ChevronDown className={`w-4 h-4 ${isSpecialist ? 'text-white/60' : 'text-gray-400'}`} />
              </button>
              
              {/* Dropdown Menu */}
              {userMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {user?.userType === 'SPECIALIST' ? (
                      <>
                        <Link
                          to="/specialist/profile/edit"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User className="w-4 h-4 mr-2" />
                          Profilul meu
                        </Link>
                        <Link
                          to="/specialist/settings"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Setări Business
                        </Link>
                        <Link
                          to="/help"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <HelpCircle className="w-4 h-4 mr-2" />
                          Ajutor
                        </Link>
                        <hr className="my-1" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Deconectare
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User className="w-4 h-4 mr-2" />
                          Profilul Meu
                        </Link>

                        <Link
                          to="/subscriptions"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Abonamente & Credite
                        </Link>
                        <Link
                          to="/settings"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Setări
                        </Link>
                        <Link
                          to="/help"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <HelpCircle className="w-4 h-4 mr-2" />
                          Ajutor
                        </Link>
                        <hr className="my-1" />
                        {user?.role === 'ADMIN' && (
                          <>
                            <Link
                              to="/admin"
                              className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Admin Panel
                            </Link>
                            <hr className="my-1" />
                          </>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Deconectare
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${isSpecialist ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600'}`}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu - slide-in drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 md:hidden"
            style={{ zIndex: 9998 }}
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div
            className="fixed top-0 right-0 bottom-0 w-72 bg-white md:hidden flex flex-col shadow-2xl"
            style={{ zIndex: 9999, paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Drawer header */}
            <div className={`flex items-center justify-between px-4 py-4 border-b ${isSpecialist ? 'bg-teal-700 border-teal-800' : 'bg-blue-600 border-gray-100'}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{user?.firstName} {user?.lastName}</p>
                  <p className={`text-xs ${isSpecialist ? 'text-teal-200' : 'text-blue-200'}`}>{user?.email}</p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable links */}
            <div className="flex-1 overflow-y-auto py-2">
              {user?.userType === 'SPECIALIST' ? (
                // Specialist Mobile Menu
                <div className="px-2 space-y-0.5">
                  {[
                    { to: '/specialist/dashboard', icon: Home, label: 'Dashboard' },
                    { to: '/specialist/profile/edit', icon: User, label: 'Profilul Meu' },
                    { to: '/specialists', icon: MapPin, label: 'Hartă Specialiști' },
                    { to: '/specialist/settings', icon: Settings, label: 'Setări Business' },
                    { to: '/help', icon: HelpCircle, label: 'Ajutor' },
                  ].map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}
                </div>
              ) : (
                // Patient Mobile Menu - grouped sections
                <div className="px-2">
                  {/* Main */}
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-blue-600 bg-blue-50 mb-1"
                  >
                    <Home className="w-4 h-4 flex-shrink-0" />
                    Dashboard
                  </Link>

                  {/* Profil */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Profil</p>

                  {[
                    { to: '/profile', icon: User, label: 'Profilul Meu' },
                  ].map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}

                  {/* Nutriție */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Nutriție</p>
                  {[
                    { to: '/nutrition', icon: Apple, label: 'Plan Nutriție' },
                    { to: '/food-tracking', icon: Utensils, label: 'Mese & Calorii cu AI' },
                    { to: '/food-community', icon: Flame, label: 'Retete Comunitate' },
                  ].map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}

                  {/* Antrenament */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Antrenament & Tracking</p>
                  {[
                    { to: '/exercises', icon: Dumbbell, label: 'Exerciții Video' },
                    { to: '/goals', icon: Target, label: 'Obiective Fitness' },
                    { to: '/challenges', icon: Trophy, label: 'Provocări' },
                    { to: '/water-intake', icon: Droplets, label: 'Aport Apă' },
                    { to: '/insights', icon: Sparkles, label: 'AI Insights' },
                    { to: '/progress-photos', icon: Camera, label: 'Fotografii Progres' },
                    { to: '/journal', icon: BookOpen, label: 'Jurnal (Tranzit & Notițe)' },
                  ].map(({ to, icon: Icon, label }) => (

                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}

                  {/* Resurse */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Resurse</p>
                  {[
                    { to: '/chat', icon: MessageCircle, label: 'Nutriționist AI' },
                    { to: '/specialists', icon: MapPin, label: 'Hartă Specialiști' },
                    { to: '/education', icon: BookOpen, label: 'Educație Fitness' },
                  ].map(({ to, icon: Icon, label }) => (


                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}

                  {/* Notificari */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Notificari</p>
                  <Link
                    to="/notifications"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                  >
                    <Bell className="w-4 h-4 flex-shrink-0 text-purple-500" />
                    Notificari & Remindere
                  </Link>

                  {/* Cont */}
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cont</p>
                  {[
                    { to: '/subscriptions', icon: CreditCard, label: 'Abonamente & Credite' },
                    { to: '/settings', icon: Settings, label: 'Setari' },
                    { to: '/help', icon: HelpCircle, label: 'Ajutor' },
                    ...(user?.role === 'ADMIN' ? [{ to: '/admin', icon: Settings, label: '⚙️ Admin Panel' }] : []),
                  ].map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Logout - always visible at bottom */}
            <div className="border-t border-gray-100 px-2 py-3">
              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                Deconectează-te
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
